import { z } from 'zod';

import { FlagKindSchema } from '../enums';
import { IdSchema } from './common';

/**
 * Очередь флагов в админке (BLUEPRINT Stage 9, задача 6): жалобы участников (`member`),
 * отметки капитана, флаги из обратной связи (`feedback`) и системные эскалации неявок.
 * Решение оператора всегда пишется в `AuditLog`.
 */

export const FlagSourceSchema = z.enum(['feedback', 'captain', 'member', 'system']);
export type FlagSource = z.infer<typeof FlagSourceSchema>;

export const AdminFlagsListInputSchema = z.object({
  resolved: z.boolean().default(false),
  kind: z.array(FlagKindSchema).max(5).optional(),
  source: z.array(FlagSourceSchema).max(4).optional(),
  userId: IdSchema.optional(),
  squadId: IdSchema.optional(),
  /** Имя, username или Telegram ID участника, на кого флаг */
  search: z.string().trim().max(60).optional(),
  cursor: IdSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export type AdminFlagsListInput = z.infer<typeof AdminFlagsListInputSchema>;

/** Что оператор делает с флагом. */
export const FlagResolutionSchema = z.enum(['dismiss', 'warn', 'remove']);
export type FlagResolution = z.infer<typeof FlagResolutionSchema>;

export const AdminFlagResolveInputSchema = z
  .object({
    flagId: IdSchema,
    resolution: FlagResolutionSchema,
    comment: z.string().trim().max(500).optional(),
    /** `remove`: доля возврата за оставшиеся сборы сезона, % (0 — без возврата) */
    refundPct: z.number().int().min(0).max(100).optional(),
    /** `remove`: вернуть участника в пул города */
    backToPool: z.boolean().default(false),
  })
  .refine((v) => v.resolution !== 'remove' || v.refundPct !== undefined, {
    message: 'Укажи процент возврата',
    path: ['refundPct'],
  });
export type AdminFlagResolveInput = z.infer<typeof AdminFlagResolveInputSchema>;

const FlagPersonSchema = z.object({
  id: IdSchema,
  firstName: z.string(),
  lastName: z.string().nullable(),
  username: z.string().nullable(),
  tgId: z.string(),
});

/** Строка очереди флагов: на кого, от кого, по какому сбору, что решил оператор. */
export const AdminFlagRowSchema = z.object({
  id: IdSchema,
  kind: FlagKindSchema,
  source: FlagSourceSchema,
  text: z.string().nullable(),
  createdAt: z.string().datetime(),
  resolved: z.boolean(),
  resolution: z.string().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  user: FlagPersonSchema,
  /** Скрыт от всех, кроме ops: автор флага из обратной связи (BLUEPRINT 2.2.3) */
  reporter: FlagPersonSchema.nullable(),
  /** Скор надёжности участника на момент запроса и сколько у него неявок */
  reliability: z.number().int(),
  noShows: z.number().int().nonnegative(),
  /** Сколько ещё неразобранных флагов на этого же участника */
  openFlags: z.number().int().nonnegative(),
  gathering: z
    .object({
      id: IdSchema,
      index: z.number().int(),
      startsAt: z.string().datetime(),
      activityName: z.string(),
    })
    .nullable(),
  squad: z
    .object({ id: IdSchema, title: z.string(), seasonId: IdSchema.nullable() })
    .nullable(),
  /** Заказ сезона участника: сколько оплачено и сколько уже возвращено */
  order: z
    .object({
      id: IdSchema,
      amountKopecks: z.number().int(),
      refundedKopecks: z.number().int(),
      gatheringsLeft: z.number().int().nonnegative(),
      gatheringsTotal: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type AdminFlagRow = z.infer<typeof AdminFlagRowSchema>;

export const AdminFlagsListResultSchema = z.object({
  rows: z.array(AdminFlagRowSchema),
  nextCursor: IdSchema.nullable(),
  /** Сколько неразобранных флагов всего — бейдж в меню */
  openCount: z.number().int().nonnegative(),
});
export type AdminFlagsListResult = z.infer<typeof AdminFlagsListResultSchema>;

export const AdminFlagResolveResultSchema = z.object({
  flagId: IdSchema,
  resolution: FlagResolutionSchema,
  /** Сколько вернули по заказу, копейки (0 — возврата не было) */
  refundedKopecks: z.number().int().nonnegative(),
  removedFromSquad: z.boolean(),
});
export type AdminFlagResolveResult = z.infer<typeof AdminFlagResolveResultSchema>;
